import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Monitor, Smartphone, BarChart3, TrendingUp, Laptop, Server, Database, Code, Cpu, Activity, Package, Cloud, Wifi, HardDrive, Zap, Settings, Terminal, Layers, Globe, Lock, Mail, Share2, Shield, FileText, Folder, GitBranch, Clipboard, Bell, Calendar, Users, Search, Filter, Download, Upload } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [particles, setParticles] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [ripples, setRipples] = useState([]);
  const [iconPositions, setIconPositions] = useState([]);
  const [cardMousePosition, setCardMousePosition] = useState({ x: 50, y: 50 });
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Generate random icon positions that are well-distributed
  useEffect(() => {
    const generateRandomPositions = () => {
      const positions = [];
      const minDistance = 15; // Reduced minimum distance to fit more icons
      const iconCount = 30; // Increased from 18 to 30 icons

      // Define the login box exclusion zone (approximate center area)
      const excludeZone = {
        xMin: 30, // 30% from left
        xMax: 70, // 70% from left (40% width)
        yMin: 25, // 25% from top
        yMax: 75, // 75% from top (50% height)
      };

      const isInExclusionZone = (pos) => {
        return pos.x >= excludeZone.xMin && pos.x <= excludeZone.xMax &&
               pos.y >= excludeZone.yMin && pos.y <= excludeZone.yMax;
      };

      const isPositionValid = (newPos, existingPositions) => {
        // Check if position is in exclusion zone
        if (isInExclusionZone(newPos)) {
          return false;
        }
        
        // Check distance from other icons
        return existingPositions.every(pos => {
          const dx = newPos.x - pos.x;
          const dy = newPos.y - pos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          return distance >= minDistance;
        });
      };

      let attempts = 0;
      const maxAttempts = 1000; // Increased attempts for more icons

      while (positions.length < iconCount && attempts < maxAttempts) {
        const newPos = {
          x: Math.random() * 90 + 5, // 5% to 95%
          y: Math.random() * 90 + 5, // 5% to 95%
        };

        if (isPositionValid(newPos, positions)) {
          positions.push(newPos);
        }
        attempts++;
      }

      return positions;
    };

    setIconPositions(generateRandomPositions());
  }, []);

  // Generate floating particles (reduced for performance)
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 25; i++) {
        newParticles.push({
          id: i,
          left: Math.random() * 100,
          top: Math.random() * 100,
          size: Math.random() * 6 + 2,
          duration: Math.random() * 25 + 15,
          delay: Math.random() * 8,
          opacity: Math.random() * 0.5 + 0.2,
        });
      }
      setParticles(newParticles);
    };
    generateParticles();
  }, []);

  // Generate water bubbles floating up
  useEffect(() => {
    const generateBubbles = () => {
      const newBubbles = [];
      for (let i = 0; i < 15; i++) {
        newBubbles.push({
          id: i,
          left: Math.random() * 100,
          bottom: -10,
          size: Math.random() * 40 + 20, // 20-60px
          duration: Math.random() * 10 + 15, // 15-25 seconds
          delay: Math.random() * 10, // 0-10 seconds delay
        });
      }
      setBubbles(newBubbles);
    };
    generateBubbles();
  }, []);

  // Mouse move effect for interactive background (optimized)
  useEffect(() => {
    if (iconPositions.length === 0) return; // Wait for positions to be generated
    
    let lastRippleTime = 0;
    const rippleDelay = 100; // Minimum ms between ripples - reduced for more responsiveness
    
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      
      setMousePosition({ x, y });
      
      // Throttle ripple creation for better performance
      const now = Date.now();
      if (now - lastRippleTime < rippleDelay) return;
      
      // Check if mouse is near any icon and create ripple effect
      iconPositions.forEach((pos, index) => {
        const iconX = pos.x;
        const iconY = pos.y;
        
        // Calculate distance from mouse to icon
        const distance = Math.sqrt(Math.pow(x - iconX, 2) + Math.pow(y - iconY, 2));
        
        // If mouse is within 18 units of an icon, create ripple - increased range and higher trigger rate
        if (distance < 18 && Math.random() > 0.5) {
          const newRipple = {
            id: Date.now() + index,
            x: iconX,
            y: iconY,
          };
          
          setRipples(prev => {
            // Limit ripples to max 8 for better visual effect
            if (prev.length >= 8) return prev;
            return [...prev, newRipple];
          });
          
          lastRippleTime = now;
          
          // Remove ripple after animation
          setTimeout(() => {
            setRipples(prev => prev.filter(r => r.id !== newRipple.id));
          }, 2500);
        }
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [iconPositions]);

  // Handle mouse movement over the card for interactive background
  const handleCardMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCardMousePosition({ x, y });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/');
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Creative Animated Background - Indigo/Blue Theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-blue-950">
        
        {/* Interactive gradient that follows mouse */}
        <div 
          className="absolute inset-0 opacity-40 transition-all duration-700 ease-out"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(99, 102, 241, 0.25) 0%, transparent 50%)`
          }}
        />
        
        {/* Animated mesh gradient layers */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/20 via-blue-600/15 to-violet-600/20 animate-gradient-xy"></div>
          <div className="absolute inset-0 bg-gradient-to-bl from-blue-600/15 via-indigo-600/10 to-purple-600/15 animate-gradient-xy-reverse"></div>
        </div>

        {/* Dynamic particles with varying opacity */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-br from-indigo-400/40 to-blue-400/40 animate-float-particle backdrop-blur-sm"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`,
              opacity: particle.opacity,
              boxShadow: `0 0 ${particle.size * 2}px rgba(99, 102, 241, ${particle.opacity})`,
            }}
          />
        ))}

        {/* Water bubbles floating up */}
        {bubbles.map((bubble) => (
          <div
            key={`bubble-${bubble.id}`}
            className="bubble"
            style={{
              left: `${bubble.left}%`,
              bottom: `${bubble.bottom}%`,
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              animationDuration: `${bubble.duration}s`,
              animationDelay: `${bubble.delay}s`,
            }}
          />
        ))}
        
        {/* Interactive ripple effects */}
        {ripples.map((ripple) => (
          <div
            key={ripple.id}
            className="absolute pointer-events-none"
            style={{
              left: `${ripple.x}%`,
              top: `${ripple.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="ripple-wave"></div>
            <div className="ripple-wave delay-200"></div>
            <div className="ripple-wave delay-400"></div>
          </div>
        ))}
        
        {/* Industry & Software icons - Randomly positioned */}
        <div className="absolute inset-0">
          {iconPositions.length > 0 && (() => {
            const icons = [
              { Icon: BarChart3, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate', size: 48 },
              { Icon: Laptop, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate-reverse', size: 50 },
              { Icon: Server, animation: 'animate-orbit-3', extraClass: 'animate-pulse-scale', size: 54 },
              { Icon: Database, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate', size: 46 },
              { Icon: Code, animation: 'animate-orbit-2', extraClass: 'animate-pulse-scale', size: 48 },
              { Icon: Activity, animation: 'animate-orbit-3', extraClass: 'animate-float-rotate-reverse', size: 42 },
              { Icon: HardDrive, animation: 'animate-orbit-1', extraClass: 'animate-drift', size: 44 },
              { Icon: Terminal, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate', size: 46 },
              { Icon: Layers, animation: 'animate-orbit-3', extraClass: 'animate-pulse-scale', size: 40 },
              { Icon: Monitor, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate-reverse', size: 52 },
              { Icon: Smartphone, animation: 'animate-orbit-3', extraClass: 'animate-drift', size: 44 },
              { Icon: TrendingUp, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate', size: 46 },
              { Icon: Cpu, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate-reverse', size: 44 },
              { Icon: Package, animation: 'animate-orbit-3', extraClass: 'animate-pulse-scale', size: 45 },
              { Icon: Cloud, animation: 'animate-orbit-1', extraClass: 'animate-drift', size: 48 },
              { Icon: Wifi, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate-reverse', size: 42 },
              { Icon: Settings, animation: 'animate-orbit-3', extraClass: 'animate-pulse-scale', size: 44 },
              { Icon: Zap, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate', size: 40 },
              { Icon: Globe, animation: 'animate-orbit-2', extraClass: 'animate-drift', size: 46 },
              { Icon: Lock, animation: 'animate-orbit-3', extraClass: 'animate-pulse-scale', size: 42 },
              { Icon: Mail, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate-reverse', size: 44 },
              { Icon: Share2, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate', size: 40 },
              { Icon: Shield, animation: 'animate-orbit-3', extraClass: 'animate-drift', size: 48 },
              { Icon: FileText, animation: 'animate-orbit-1', extraClass: 'animate-pulse-scale', size: 42 },
              { Icon: Folder, animation: 'animate-orbit-2', extraClass: 'animate-float-rotate-reverse', size: 44 },
              { Icon: GitBranch, animation: 'animate-orbit-3', extraClass: 'animate-float-rotate', size: 46 },
              { Icon: Clipboard, animation: 'animate-orbit-1', extraClass: 'animate-drift', size: 40 },
              { Icon: Bell, animation: 'animate-orbit-2', extraClass: 'animate-pulse-scale', size: 42 },
              { Icon: Calendar, animation: 'animate-orbit-3', extraClass: 'animate-float-rotate-reverse', size: 44 },
              { Icon: Users, animation: 'animate-orbit-1', extraClass: 'animate-float-rotate', size: 48 },
            ];

            // Random effect generators for each icon
            const randomEffects = [
              'fade-in-out', 
              'zoom-pulse', 
              'flash-subtle', 
              'opacity-wave',
              'scale-breathe',
              'glow-pulse'
            ];

            return iconPositions.map((pos, index) => {
              if (index >= icons.length) return null;
              const { Icon, animation, extraClass, size } = icons[index];
              
              // Assign random effect and timing to each icon
              const randomEffect = randomEffects[Math.floor(Math.random() * randomEffects.length)];
              const randomDuration = (Math.random() * 4 + 3).toFixed(2); // 3-7 seconds
              const randomDelay = (Math.random() * 5).toFixed(2); // 0-5 seconds delay
              
              return (
                <div 
                  key={index}
                  className={`absolute ${animation} icon-interactive icon-${randomEffect}`}
                  style={{ 
                    left: `${pos.x}%`, 
                    top: `${pos.y}%`,
                    animationDuration: `${randomDuration}s`,
                    animationDelay: `${randomDelay}s`,
                  }}
                >
                  <Icon 
                    className={`text-white/25 ${extraClass}`}
                    style={{ width: `${size}px`, height: `${size}px` }}
                  />
                </div>
              );
            });
          })()}
        </div>

        {/* Morphing gradient orbs with indigo/blue theme */}
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-br from-indigo-600/20 via-blue-600/15 to-transparent rounded-full blur-3xl animate-morph-1"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-600/20 via-indigo-600/15 to-transparent rounded-full blur-3xl animate-morph-2"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-violet-600/12 via-purple-600/10 to-transparent rounded-full blur-3xl animate-morph-3"></div>
        
        {/* Animated scanlines for tech feel */}
        <div className="absolute inset-0 bg-scanlines opacity-5 animate-scan"></div>
        
        {/* Enhanced grid with perspective */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        {/* Creative accent lines - indigo theme */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-500/60 to-transparent animate-shimmer"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Creative Card with 3D effect */}
        <div className="perspective-container">
          <Card 
            className="shadow-2xl border-slate-700/50 backdrop-blur-xl bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90 hover:shadow-indigo-500/20 transition-all duration-500 transform hover:scale-[1.02] card-3d relative overflow-hidden"
            onMouseMove={handleCardMouseMove}
            onMouseEnter={() => setIsCardHovered(true)}
            onMouseLeave={() => setIsCardHovered(false)}
          >
            {/* Interactive gradient overlay that follows mouse on card */}
            <div 
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-lg"
              style={{
                background: `radial-gradient(circle 400px at ${cardMousePosition.x}% ${cardMousePosition.y}%, rgba(99, 102, 241, 0.15) 0%, rgba(59, 130, 246, 0.08) 30%, transparent 70%)`
              }}
            />
            
            {/* Animated mesh pattern overlay */}
            <div 
              className={`absolute inset-0 opacity-0 transition-opacity duration-700 pointer-events-none rounded-lg ${isCardHovered ? 'opacity-20' : ''}`}
              style={{
                background: `
                  repeating-linear-gradient(
                    ${cardMousePosition.x}deg,
                    rgba(99, 102, 241, 0.03) 0px,
                    transparent 2px,
                    transparent 10px
                  ),
                  repeating-linear-gradient(
                    ${90 + cardMousePosition.y}deg,
                    rgba(59, 130, 246, 0.03) 0px,
                    transparent 2px,
                    transparent 10px
                  )
                `
              }}
            />
            
            {/* Glowing orb that follows mouse */}
            {isCardHovered && (
              <div 
                className="absolute w-96 h-96 pointer-events-none rounded-full blur-3xl transition-all duration-300"
                style={{
                  left: `${cardMousePosition.x}%`,
                  top: `${cardMousePosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(59, 130, 246, 0.05) 40%, transparent 70%)',
                }}
              />
            )}
            
            {/* Spotlight effect */}
            <div 
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-lg mix-blend-overlay"
              style={{
                background: `radial-gradient(circle 300px at ${cardMousePosition.x}% ${cardMousePosition.y}%, rgba(255, 255, 255, 0.05) 0%, transparent 60%)`
              }}
            />
            
            <CardHeader className="space-y-4 sm:space-y-5 text-center pb-4 sm:pb-6 relative">
              {/* Animated background accent */}
              <div className="absolute top-0 left-0 right-0 h-24 sm:h-32 bg-gradient-to-b from-indigo-500/5 to-transparent rounded-t-lg"></div>
              
              {/* Creative logo with multiple layers */}
              <div className="mx-auto w-16 sm:w-20 h-16 sm:h-20 relative group">
                {/* Outer rotating ring */}
                <div className="absolute inset-0 rounded-full border-2 border-indigo-500/30 animate-spin-slow"></div>
                <div className="absolute inset-2 rounded-full border-2 border-blue-500/20 animate-spin-reverse"></div>
                
                {/* Logo container - centered properly */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-indigo-600 via-indigo-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30 relative overflow-hidden group-hover:shadow-indigo-400/50 transition-all duration-300">
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    <span className="text-white text-lg sm:text-xl font-bold relative z-10">PD</span>
                  </div>
                </div>
                
                {/* Pulsing dots around logo */}
                <div className="absolute -top-1 -right-1 w-2 sm:w-3 h-2 sm:h-3 bg-indigo-400 rounded-full animate-ping"></div>
                <div className="absolute -bottom-1 -left-1 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-blue-400 rounded-full animate-pulse delay-75"></div>
              </div>
              
              {/* Creative typography with animation */}
              <div className="space-y-1 sm:space-y-2 relative z-10">
                <CardTitle className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  <span className="inline-block animate-text-reveal">Pulse</span>{' '}
                  <span className="inline-block animate-text-reveal delay-100 bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Desk</span>
                </CardTitle>
                <CardDescription className="text-slate-400 text-xs sm:text-sm font-medium flex items-center justify-center gap-2">
                  <span className="w-1 h-1 bg-indigo-500 rounded-full animate-pulse"></span>
                  Monitoring & Analytics Portal
                  <span className="w-1 h-1 bg-blue-500 rounded-full animate-pulse delay-300"></span>
                </CardDescription>
              </div>
              
              {/* Creative divider with animation */}
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-6 sm:w-8 bg-gradient-to-r from-transparent to-indigo-500/50 animate-expand"></div>
                <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 bg-indigo-500 rounded-full animate-pulse"></div>
                <div className="h-px w-6 sm:w-8 bg-gradient-to-l from-transparent to-indigo-500/50 animate-expand"></div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-1 sm:pt-2 relative">
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-5" data-testid="login-form">
                <div className="space-y-1.5 sm:space-y-2 group">
                  <Label htmlFor="username" className="text-slate-300 text-xs sm:text-sm font-medium flex items-center gap-2">
                    <span className="w-1 h-1 bg-indigo-500 rounded-full opacity-0 group-focus-within:opacity-100 transition-opacity"></span>
                    Username
                  </Label>
                  <div className="relative">
                    <Input
                      id="username"
                      data-testid="username-input"
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className="h-10 sm:h-12 bg-slate-800/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:bg-slate-800/70 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all pl-3 sm:pl-4 pr-8 sm:pr-10 text-xs sm:text-sm"
                    />
                    <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-600 rounded-full opacity-0 focus-within:opacity-100 focus-within:bg-indigo-500 transition-all"></div>
                  </div>
                </div>
                
                <div className="space-y-1.5 sm:space-y-2 group">
                  <Label htmlFor="password" className="text-slate-300 text-xs sm:text-sm font-medium flex items-center gap-2">
                    <span className="w-1 h-1 bg-indigo-500 rounded-full opacity-0 group-focus-within:opacity-100 transition-opacity"></span>
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-10 sm:h-12 bg-slate-800/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:bg-slate-800/70 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all pl-3 sm:pl-4 pr-8 sm:pr-10 text-xs sm:text-sm"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-600 rounded-full opacity-0 focus-within:opacity-100 focus-within:bg-indigo-500 transition-all"></div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    className="w-full h-12 bg-gradient-to-r from-indigo-600 via-indigo-500 to-blue-600 hover:from-indigo-500 hover:via-indigo-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-indigo-900/40 hover:shadow-indigo-700/50 transition-all duration-300 border-0 relative overflow-hidden group"
                    disabled={loading}
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Sign In
                        <span className="text-lg">→</span>
                      </span>
                    )}
                  </Button>
                </div>
              </form>
              
              {/* Creative footer */}
              <div className="mt-6 pt-6 border-t border-slate-700/50 relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 bg-slate-900">
                  <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full"></div>
                </div>
                <p className="text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                  <svg className="w-3 h-3 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure 256-bit encrypted connection
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Creative branding badge with animation */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/50 backdrop-blur-sm border border-slate-700/50">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
            </span>
            <p className="text-xs text-slate-400 font-medium">Powered by Acuta Analytics</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;